import { useLayoutEffect, useRef, type RefObject } from 'react';

const MOVE_MS = 520;
const EASE = 'cubic-bezier(.22, .8, .24, 1)';
/** これ以上動いたカードは、元の位置に残像を出してから滑らせる。 */
const GHOST_THRESHOLD_PX = 40;

interface Snapshot {
  rect: DOMRect;
  html: string;
  className: string;
}

/**
 * カードの移動モーション。確定済み画面と同じ見え方を保つ。
 *
 * ・列をまたいで移動したカードは、元の位置から新しい位置へ滑って現れる
 * ・押し出された／詰められた他のカードも、ずれた分を補間する
 * ・prefers-reduced-motion のときは動かさない
 */
export function useCardMotion(viewRef: RefObject<HTMLElement | null>, key: unknown): void {
  const prev = useRef<Map<string, Snapshot>>(new Map());
  const first = useRef(true);

  useLayoutEffect(() => {
    const view = viewRef.current;
    if (!view) return;

    const cards = [...view.querySelectorAll<HTMLElement>('.card[data-id]')];
    const next = new Map<string, Snapshot>();
    for (const card of cards) {
      const id = card.dataset.id;
      if (!id) continue;
      next.set(id, {
        rect: card.getBoundingClientRect(),
        html: card.innerHTML,
        className: card.className,
      });
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (first.current || reduceMotion) {
      first.current = false;
      prev.current = next;
      return;
    }

    const ghosts: HTMLElement[] = [];
    for (const card of cards) {
      const id = card.dataset.id;
      if (!id) continue;
      const before = prev.current.get(id);
      const after = next.get(id);
      if (!before || !after) continue;

      const dx = before.rect.left - after.rect.left;
      const dy = before.rect.top - after.rect.top;
      if (Math.abs(dx) < 2 && Math.abs(dy) < 2) continue;

      const moved = Math.abs(dx) > GHOST_THRESHOLD_PX || Math.abs(dy) > GHOST_THRESHOLD_PX;

      if (moved) {
        // 元のカードの見た目を画面上に固定した複製。移動しながら消える。
        const ghost = document.createElement('article');
        ghost.className = `${before.className} fly-ghost`;
        ghost.innerHTML = before.html;
        Object.assign(ghost.style, {
          left: `${before.rect.left}px`,
          top: `${before.rect.top}px`,
          width: `${before.rect.width}px`,
        });
        document.body.append(ghost);
        ghosts.push(ghost);
        ghost
          .animate(
            [
              { transform: 'none', opacity: 1 },
              { opacity: 0, offset: 0.45 },
              { transform: `translate(${-dx}px, ${-dy}px)`, opacity: 0 },
            ],
            { duration: MOVE_MS, easing: EASE },
          )
          .finished.then(
            () => ghost.remove(),
            () => ghost.remove(),
          );

        card.classList.add('is-flying');
        card
          .animate(
            [
              { transform: `translate(${dx}px, ${dy}px)`, opacity: 0 },
              { opacity: 1, offset: 0.45 },
              { transform: 'none', opacity: 1 },
            ],
            { duration: MOVE_MS, easing: EASE },
          )
          .finished.then(
            () => card.classList.remove('is-flying'),
            () => card.classList.remove('is-flying'),
          );
      } else {
        card.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], {
          duration: MOVE_MS,
          easing: EASE,
        });
      }
    }

    prev.current = next;
    return () => {
      for (const g of ghosts) g.remove();
    };
    // key が変わるたびに、直前の配置と比べ直す
  }, [viewRef, key]);
}
